import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/store/auth';
import type { IssuanceLog, PurchaseOrder, Vendor } from '@/lib/types';
import {
  Banner,
  Card,
  Field,
  Modal,
  ModalHeader,
  PageHeader,
  Stat,
  Tag,
  cellStyle,
  inputStyle,
  useToast,
} from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { Select } from '@/components/Select';
import { useSort, SortTh } from '@/components/sortable';
import { SearchInput, matches } from '@/components/SearchInput';
import { ntd } from '@/lib/format';

const PO_STATUS: Record<string, string> = {
  PENDING: '待確認',
  CONFIRMED: '已確認',
  RECEIVED: '已入庫',
  CANCELLED: '已取消',
};

export function SuppliersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'TENANT_ADMIN' || user?.role === 'STORE_MANAGER';
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const [vendorOpen, setVendorOpen] = useState(false);
  const [qVendor, setQVendor] = useState('');
  const [vendor, setVendor] = useState({ name: '', country: 'TW', score: 80, joint: false });
  const [poOpen, setPoOpen] = useState(false);
  const [po, setPo] = useState({ vendorId: '', amount: 0 });

  const vendors = useQuery({ queryKey: ['vendors'], queryFn: () => api.get<Vendor[]>('/inventory/vendors') });
  const orders = useQuery({ queryKey: ['purchase-orders'], queryFn: () => api.get<PurchaseOrder[]>('/inventory/purchase-orders') });
  const issuances = useQuery({ queryKey: ['issuances'], queryFn: () => api.get<IssuanceLog[]>('/inventory/issuances') });

  const createVendor = useMutation({
    mutationFn: () =>
      api.post('/inventory/vendors', {
        name: vendor.name,
        country: vendor.country,
        score: Number(vendor.score),
        joint: vendor.joint,
      }),
    onSuccess: () => {
      ok('供應商已建立');
      setVendorOpen(false);
      setVendor({ name: '', country: 'TW', score: 80, joint: false });
      void qc.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: fail,
  });
  const createPO = useMutation({
    mutationFn: () =>
      api.post('/inventory/purchase-orders', {
        vendorId: po.vendorId,
        amountCents: Number(po.amount) * 100,
      }),
    onSuccess: () => {
      ok('採購單已建立');
      setPoOpen(false);
      setPo({ vendorId: '', amount: 0 });
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
    onError: fail,
  });

  const vendorList = vendors.data ?? [];
  const shownVendors = qVendor ? vendorList.filter((v) => matches(v.name, qVendor) || matches(v.country ?? '', qVendor)) : vendorList;
  const poList = orders.data ?? [];
  const issList = issuances.data ?? [];
  const vName = (id: string) => vendorList.find((v) => v.id === id)?.name ?? id;

  const sortVendors = useSort(shownVendors, {
    name: (v) => v.name,
    country: (v) => v.country,
    score: (v) => v.score,
    joint: (v) => (v.joint ? 1 : 0),
  });
  const sortPO = useSort(poList, {
    vendor: (p) => vName(p.vendorId),
    amount: (p) => p.amountCents,
    status: (p) => PO_STATUS[p.status],
  });
  const sortIssuance = useSort(issList, {
    item: (i) => i.itemName,
    qty: (i) => i.qty,
    type: (i) => i.type,
    chain: (i) => (i.flagged ? 1 : 0),
  });

  return (
    <div>
      <PageHeader title="供應商 / 領取" subtitle="供應商評鑑 · 採購單 · 出庫資料鏈完整性稽核" />
      <Banner msg={msg} />

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="供應商" value={String(vendorList.length)} tone="primary" />
        <Stat label="採購單" value={String(poList.length)} />
        <Stat label="出庫紀錄" value={String(issList.length)} tone="wallet" />
        <Stat label="資料鏈異常" value={String(issList.filter((i) => i.flagged).length)} tone="danger" hint="不計成本/獎金" />
      </div>

      <Card
        title="供應商"
        icon={<Icon d={I.building} size={14} style={{ color: 'var(--primary)' }} />}
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <SearchInput value={qVendor} onChange={setQVendor} placeholder="搜尋供應商 / 國別" width={200} />
            {isAdmin && (
              <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setVendorOpen(true)}>
                <Icon d={I.plus} size={12} /> 新增供應商
              </button>
            )}
          </div>
        }
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortTh label="名稱" sortKey="name" sort={sortVendors} />
              <SortTh label="國別" sortKey="country" sort={sortVendors} />
              <SortTh label="評分" sortKey="score" sort={sortVendors} align="right" />
              <SortTh label="合資" sortKey="joint" sort={sortVendors} />
            </tr>
          </thead>
          <tbody>
            {sortVendors.sorted.map((v) => (
              <tr key={v.id}>
                <td style={{ ...cellStyle, fontWeight: 600 }}>{v.name}</td>
                <td style={cellStyle} className="num">
                  {v.country}
                </td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">
                  <span style={{ color: v.score >= 80 ? 'var(--success)' : v.score >= 60 ? 'var(--warn)' : 'var(--danger)', fontWeight: 600 }}>{v.score}</span>
                </td>
                <td style={cellStyle}>{v.joint ? <Tag tone="primary">合資</Tag> : <Tag tone="default">一般</Tag>}</td>
              </tr>
            ))}
            {vendorList.length === 0 && (
              <tr>
                <td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>
                  尚無供應商
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Card
        title="採購單"
        icon={<Icon d={I.doc} size={14} style={{ color: 'var(--ink-2)' }} />}
        right={
          isAdmin && (
            <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setPoOpen(true)} disabled={vendorList.length === 0}>
              <Icon d={I.plus} size={12} /> 新增採購單
            </button>
          )
        }
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortTh label="供應商" sortKey="vendor" sort={sortPO} />
              <SortTh label="金額" sortKey="amount" sort={sortPO} align="right" />
              <SortTh label="狀態" sortKey="status" sort={sortPO} />
            </tr>
          </thead>
          <tbody>
            {sortPO.sorted.map((p) => (
              <tr key={p.id}>
                <td style={cellStyle}>{vName(p.vendorId)}</td>
                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }} className="num">
                  {ntd(p.amountCents)}
                </td>
                <td style={cellStyle}>
                  <Tag tone={p.status === 'RECEIVED' ? 'success' : p.status === 'CANCELLED' ? 'default' : 'warn'}>
                    {PO_STATUS[p.status]}
                  </Tag>
                </td>
              </tr>
            ))}
            {poList.length === 0 && (
              <tr>
                <td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={3}>
                  尚無採購單
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Card title="出庫資料鏈" icon={<Icon d={I.layers} size={14} style={{ color: 'var(--ticket)' }} />}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortTh label="品項" sortKey="item" sort={sortIssuance} />
              <SortTh label="數量" sortKey="qty" sort={sortIssuance} align="right" />
              <SortTh label="用途" sortKey="type" sort={sortIssuance} />
              <SortTh label="資料鏈" sortKey="chain" sort={sortIssuance} />
            </tr>
          </thead>
          <tbody>
            {sortIssuance.sorted.map((i) => (
              <tr key={i.id}>
                <td style={{ ...cellStyle, fontWeight: 600 }}>{i.itemName}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">
                  {i.qty}
                </td>
                <td style={cellStyle}>{i.type}</td>
                <td style={cellStyle}>
                  {i.flagged ? (
                    <Tag tone="danger" dot>
                      異常：{i.flagReason}
                    </Tag>
                  ) : (
                    <Tag tone="success" dot>
                      完整
                    </Tag>
                  )}
                </td>
              </tr>
            ))}
            {issList.length === 0 && (
              <tr>
                <td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>
                  尚無出庫紀錄
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Modal open={vendorOpen} onClose={() => setVendorOpen(false)} width={440}>
        <ModalHeader title="新增供應商" sub="僅院長 / 總監可操作" onClose={() => setVendorOpen(false)} />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="供應商名稱 *">
            <input style={inputStyle} value={vendor.name} onChange={(e) => setVendor((s) => ({ ...s, name: e.target.value }))} placeholder="台灣醫材股份" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="國別">
              <input style={inputStyle} className="num" value={vendor.country} onChange={(e) => setVendor((s) => ({ ...s, country: e.target.value }))} placeholder="TW" />
            </Field>
            <Field label="評分（0-100）">
              <input type="number" style={inputStyle} className="num" value={vendor.score} onChange={(e) => setVendor((s) => ({ ...s, score: Number(e.target.value) }))} />
            </Field>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-2)' }}>
            <input type="checkbox" checked={vendor.joint} onChange={(e) => setVendor((s) => ({ ...s, joint: e.target.checked }))} /> 合資供應商
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setVendorOpen(false)}>
            取消
          </button>
          <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!vendor.name || createVendor.isPending} onClick={() => createVendor.mutate()}>
            建立
          </button>
        </div>
      </Modal>

      <Modal open={poOpen} onClose={() => setPoOpen(false)} width={440}>
        <ModalHeader title="新增採購單" sub="選擇供應商並輸入金額" onClose={() => setPoOpen(false)} />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="供應商 *">
            <Select
              value={po.vendorId}
              onChange={(val) => setPo((s) => ({ ...s, vendorId: val }))}
              placeholder="— 選擇供應商 —"
              options={vendorList.map((v) => ({ value: v.id, label: `${v.name}` }))}
            />
          </Field>
          <Field label="金額（元）*">
            <input type="number" style={inputStyle} className="num" value={po.amount} onChange={(e) => setPo((s) => ({ ...s, amount: Number(e.target.value) }))} />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setPoOpen(false)}>
            取消
          </button>
          <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!po.vendorId || po.amount <= 0 || createPO.isPending} onClick={() => createPO.mutate()}>
            建立採購單
          </button>
        </div>
      </Modal>
    </div>
  );
}
